import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, CalendarDays, Dumbbell, LogOut, Wallet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from './ThemeToggle';
import { ChangePasswordButton } from './ChangePasswordDialog';
import { GlowBackground } from './GlowBackground';

const NAV = [
  { key: 'start', label: 'Start', to: '/dashboard', icon: Home, match: (p) => p === '/dashboard' || p.startsWith('/client/') },
  { key: 'calendar', label: 'Kalendarz', to: '/calendar', icon: CalendarDays, match: (p) => p.startsWith('/calendar') },
  { key: 'exercises', label: 'Ćwiczenia', to: '/exercises', icon: Dumbbell, match: (p) => p.startsWith('/exercises') },
  { key: 'finance', label: 'Finanse', to: '/finanse', icon: Wallet, match: (p) => p.startsWith('/finanse') },
];

export const HSMark = ({ size = 46 }) => (
  <div className="flex items-center justify-center font-black border-[1.5px] border-[var(--text-primary)] text-[var(--text-primary)] shrink-0"
    style={{ width: size, height: size, fontSize: size / 3 }} data-testid="logo-hs">HS</div>
);

const iconBtn = 'w-11 h-11 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors';

// Trainer layout (style B): floating glass icon rail on desktop, glass pill bar at the bottom on phones.
export const TrainerShell = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] relative">
      <GlowBackground />

      <aside className="hidden lg:flex fixed left-6 top-6 bottom-6 z-20 w-[88px] glass rounded-[34px] py-6 flex-col items-center gap-7" aria-label="Menu główne">
        <HSMark />
        <nav className="flex flex-col gap-2.5">
          {NAV.map(({ key, label, to, icon: Icon, match }) => (
            <button key={key} onClick={() => navigate(to)} aria-label={label} title={label}
              className={`${iconBtn} w-12 h-12 ${match(pathname) ? 'nav-active' : ''}`} data-testid={`nav-${key}`}>
              <Icon className="w-5 h-5" strokeWidth={1.6} />
            </button>
          ))}
        </nav>
        <div className="mt-auto flex flex-col items-center gap-2">
          <ThemeToggle className="w-11 h-11 border-0" />
          <ChangePasswordButton className={iconBtn} />
          <button onClick={handleLogout} aria-label="Wyloguj" title="Wyloguj" className={iconBtn} data-testid="logout-button">
            <LogOut className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>
      </aside>

      <header className="lg:hidden app-content flex items-center justify-between px-4 pt-5">
        <HSMark size={40} />
        <div className="flex items-center gap-1">
          <ThemeToggle className="w-11 h-11" />
          <ChangePasswordButton className={iconBtn} />
        </div>
      </header>

      <div className="app-content lg:pl-[132px] pb-32 lg:pb-0">{children}</div>

      <nav className="lg:hidden fixed left-4 right-4 bottom-5 z-30 glass glass-float rounded-full p-1.5 flex gap-1" aria-label="Menu główne">
        {NAV.map(({ key, label, to, icon: Icon, match }) => (
          <button key={key} onClick={() => navigate(to)}
            className={`flex-1 h-14 rounded-full flex flex-col items-center justify-center gap-1 text-[10px] font-medium tracking-wide text-[var(--text-muted)] ${match(pathname) ? 'nav-active' : ''}`}
            data-testid={`tab-${key}`}>
            <Icon className="w-[18px] h-[18px]" strokeWidth={1.6} />{label}
          </button>
        ))}
        <button onClick={handleLogout} className="flex-1 h-14 rounded-full flex flex-col items-center justify-center gap-1 text-[10px] font-medium tracking-wide text-[var(--text-muted)]" data-testid="tab-logout">
          <LogOut className="w-[18px] h-[18px]" strokeWidth={1.6} />Wyloguj
        </button>
      </nav>
    </div>
  );
};
